export type SupportedPhoneCountryId =
  | 'US'
  | 'MX'
  | 'GB'
  | 'FR'
  | 'DE'
  | 'ES'
  | 'IT'
  | 'NL'
  | 'CW'
  | 'KH'
  | 'ZA'
  | 'SC'
  | 'BE'
  | 'IE'
  | 'EE'
  | 'LV'
  | 'LT'
  | 'CH'
  | 'BG'
  | 'SI'
  | 'GR'
  | 'CY'
  | 'TRNC'
  | 'PL'
  | 'FI'
  | 'PT'
  | 'LB'
  | 'LI'
  | 'MK'
  | 'AL'
  | 'HR'
  | 'AT'
  | 'CZ';

export interface SupportedPhoneCountryConfig {
  id: number;
  shortLabel: SupportedPhoneCountryId;
  label: string;
  dialCode: string;
  placeholder: string;
  minDigits: number;
  maxDigits: number;
  active: boolean;
  default?: boolean;
  cachedAt?: number;
}

export interface SupportedPhoneCountry extends SupportedPhoneCountryConfig {
  formatLocalNumber: (digits: string) => string;
}

const getDigitsOnly = (value: string): string => value.replace(/\D/g, '');
const formatByPlaceholder = (digits: string, placeholder: string, maxDigits: number): string => {
  const trimmedDigits = digits.slice(0, maxDigits);
  if (!trimmedDigits) {
    return '';
  }

  let digitCursor = 0;
  let formattedValue = '';

  for (let index = 0; index < placeholder.length; index += 1) {
    const placeholderChar = placeholder.charAt(index);

    if (placeholderChar === '#') {
      if (digitCursor >= trimmedDigits.length) {
        break;
      }
      formattedValue += trimmedDigits.charAt(digitCursor);
      digitCursor += 1;
      continue;
    }

    const hasRemainingDigitPlaceholders = placeholder.slice(index + 1).includes('#');

    if (
      digitCursor < trimmedDigits.length &&
      (digitCursor > 0 || (formattedValue.length === 0 && hasRemainingDigitPlaceholders))
    ) {
      formattedValue += placeholderChar;
    }
  }

  if (digitCursor < trimmedDigits.length) {
    formattedValue += trimmedDigits.slice(digitCursor);
  }

  return formattedValue;
};

export const buildSupportedPhoneCountries = (
  countryConfigs?: SupportedPhoneCountryConfig[]
): SupportedPhoneCountry[] => {
  const activeCountryConfigs = [...(countryConfigs ?? [])]
    .filter((country) => country.active)
    .sort(
      (leftCountry, rightCountry) =>
        Number(Boolean(rightCountry.default)) - Number(Boolean(leftCountry.default))
    );

  return activeCountryConfigs.map((country) => ({
    ...country,
    formatLocalNumber: (digits: string) =>
      formatByPlaceholder(digits, country.placeholder, country.maxDigits),
  }));
};

export const supportedPhoneCountries: SupportedPhoneCountry[] = [];

export const getDefaultSupportedPhoneCountry = (
  countries: SupportedPhoneCountry[] = supportedPhoneCountries
): SupportedPhoneCountry | undefined => {
  const browserPreferredCountryId = getBrowserPreferredPhoneCountryId(countries);

  if (browserPreferredCountryId) {
    return countries.find((country) => country.shortLabel === browserPreferredCountryId);
  }

  return countries.find((country) => country.shortLabel === 'US') ?? countries[0];
};

export const getDefaultSupportedPhoneCountryId = (
  countries: SupportedPhoneCountry[] = supportedPhoneCountries
): SupportedPhoneCountryId | undefined => {
  return getDefaultSupportedPhoneCountry(countries)?.shortLabel;
};

const getBrowserLocaleCandidates = (): string[] => {
  if (typeof navigator === 'undefined') {
    return [];
  }

  const browserLocales = Array.isArray(navigator.languages) && navigator.languages.length > 0
    ? navigator.languages
    : [navigator.language];

  return browserLocales.filter((locale): locale is string => typeof locale === 'string');
};

const getCountryIdFromLocale = (locale: string): SupportedPhoneCountryId | undefined => {
  const normalizedLocale = locale.trim();
  if (!normalizedLocale) {
    return undefined;
  }

  const regionMatch = normalizedLocale.match(/[-_]([A-Za-z]{2})\b/);
  const regionCode = regionMatch?.[1]?.toUpperCase();

  switch (regionCode) {
    case 'US':
      return 'US';
    case 'MX':
      return 'MX';
    case 'GB':
      return 'GB';
    case 'FR':
      return 'FR';
    case 'DE':
      return 'DE';
    case 'ES':
      return 'ES';
    case 'IT':
      return 'IT';
    case 'NL':
      return 'NL';
    case 'CW':
      return 'CW';
    case 'KH':
      return 'KH';
    case 'ZA':
      return 'ZA';
    case 'SC':
      return 'SC';
    case 'BE':
      return 'BE';
    case 'IE':
      return 'IE';
    case 'EE':
      return 'EE';
    case 'LV':
      return 'LV';
    case 'LT':
      return 'LT';
    case 'CH':
      return 'CH';
    case 'BG':
      return 'BG';
    case 'SI':
      return 'SI';
    case 'GR':
      return 'GR';
    case 'CY':
      return 'CY';
    case 'PL':
      return 'PL';
    case 'FI':
      return 'FI';
    case 'PT':
      return 'PT';
    case 'LB':
      return 'LB';
    case 'LI':
      return 'LI';
    case 'MK':
      return 'MK';
    case 'AL':
      return 'AL';
    case 'HR':
      return 'HR';
    case 'AT':
      return 'AT';
    case 'CZ':
      return 'CZ';
    default:
      break;
  }

  const languageCode = normalizedLocale.split(/[-_]/)[0]?.toLowerCase();

  switch (languageCode) {
    case 'en':
      return 'US';
    case 'es':
      return 'ES';
    case 'fr':
      return 'FR';
    case 'de':
      return 'DE';
    case 'it':
      return 'IT';
    case 'nl':
      return 'NL';
    case 'km':
      return 'KH';
    case 'af':
    case 'zu':
    case 'xh':
      return 'ZA';
    case 'et':
      return 'EE';
    case 'lv':
      return 'LV';
    case 'lt':
      return 'LT';
    case 'bg':
      return 'BG';
    case 'sl':
      return 'SI';
    case 'el':
      return 'GR';
    case 'pl':
      return 'PL';
    case 'fi':
      return 'FI';
    case 'pt':
      return 'PT';
    case 'ar':
      return 'LB';
    case 'mk':
      return 'MK';
    case 'sq':
      return 'AL';
    case 'hr':
      return 'HR';
    case 'cs':
      return 'CZ';
    default:
      return undefined;
  }
};

export const getBrowserPreferredPhoneCountryId = (
  countries: SupportedPhoneCountry[] = supportedPhoneCountries
): SupportedPhoneCountryId | undefined => {
  const supportedCountryIds = new Set(countries.map((country) => country.shortLabel));

  for (const locale of getBrowserLocaleCandidates()) {
    const preferredCountryId = getCountryIdFromLocale(locale);
    if (preferredCountryId && supportedCountryIds.has(preferredCountryId)) {
      return preferredCountryId;
    }
  }

  return undefined;
};

export const getSupportedPhoneCountry = (
  countryId: SupportedPhoneCountryId | undefined,
  countries: SupportedPhoneCountry[] = supportedPhoneCountries
): SupportedPhoneCountry | undefined => {
  return (
    countries.find((country) => country.shortLabel === countryId) ??
    getDefaultSupportedPhoneCountry(countries)
  );
};

export const formatPhoneNumberForCountry = (
  value: string,
  country: SupportedPhoneCountry
): string => {
  const digits = getDigitsOnly(value).slice(0, country.maxDigits);
  return country.formatLocalNumber(digits);
};

export const isPhoneNumberValidForCountry = (
  value: string,
  country: SupportedPhoneCountry
): boolean => {
  const digitCount = getDigitsOnly(value).length;
  return digitCount >= country.minDigits && digitCount <= country.maxDigits;
};

export const getInternationalPhoneNumber = (
  value: string,
  country: SupportedPhoneCountry
): string => {
  return `${country.dialCode}${getDigitsOnly(value)}`;
};

export const getPhoneDisplayValue = (
  value: string,
  country: SupportedPhoneCountry
): string => {
  const formattedLocalNumber = formatPhoneNumberForCountry(value, country);
  return formattedLocalNumber ? `+${country.dialCode} ${formattedLocalNumber}` : `+${country.dialCode}`;
};

export const getPhoneInputMaxLength = (country: SupportedPhoneCountry): number => {
  return country.formatLocalNumber('9'.repeat(country.maxDigits)).length;
};